import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { floatingControlMustHide } from '@/lib/floatingControlVisibility';

/** Keep floating service controls clear of page actions, even while hidden. */
export function useFloatingControlGuard<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [obstructed, setObstructed] = useState(true);
  const { pathname } = useLocation();

  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const floating = ref.current;
      if (!floating || !floating.getClientRects().length) return;
      const controls = Array.from(document.querySelectorAll<HTMLElement>('button, a[href], [role="button"], input[type="submit"]'))
        .filter(el => !el.closest('[data-floating-control], [data-sticky-cta]') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');
      const protectedControls = controls.filter(el => el.closest('[data-hide-floating-cta]'));
      setObstructed(floatingControlMustHide(
        floating.getBoundingClientRect(),
        controls.map(el => el.getBoundingClientRect()),
        protectedControls.map(el => el.getBoundingClientRect()),
        window.innerWidth,
        window.innerHeight,
      ));
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    const mutations = new MutationObserver(schedule);
    mutations.observe(document.body, { childList: true, subtree: true });
    schedule();
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      mutations.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pathname]);

  return { ref, obstructed };
}